/**
 * Reglas del odontograma (nomenclatura FDI). Todo es puro: sin base de datos ni red,
 * para que el servidor, la app y las pruebas usen exactamente la misma lógica.
 */
import { CIE10 } from "./cie10.ts";

export const SUPERFICIES = ["O", "M", "D", "V", "L"] as const;
export type Superficie = (typeof SUPERFICIES)[number];

export const TIPOS_HALLAZGO = [
  "caries", "obturacion", "ausente", "extraccion", "corona", "endodoncia", "fractura", "diagnostico",
  "sellante", "incrustacion", "carilla", "perno", "implante", "puente", "removible",
  "erupcion", "exfoliado", "pulpotomia", "pulpectomia", "coronaacero", "mantenedor", "fluor",
  // simbología del formulario 033 del MSP
  "perdida_otra", "protesis_total", "movilidad", "recesion",
  // cirugía oral (lo que no es la extracción de un tercer molar, que ya tiene la suya)
  "incluido", "supernumerario", "quiste", "apicectomia", "traccion", "frenectomia", "alveoloplastia",
  // tratamientos de especialidad que no son de una sola pieza (se marcan como hallazgos generales)
  "ortodoncia", "armonizacion", "estetica", "atm",
] as const;
/** Hallazgos que genera la cirugía oral (el tercer molar usa la extracción de siempre): llevan su procedimiento en el plan de pagos. */
export const TIPOS_HALLAZGO_CIRUGIA: readonly string[] = ["incluido", "supernumerario", "quiste", "apicectomia", "traccion", "frenectomia", "alveoloplastia"];
/** Hallazgos que se registran con un grado (1 a 4), como en las filas del formulario 033. */
export const TIPOS_CON_GRADO: readonly string[] = ["movilidad", "recesion"];
/** Escalas: la movilidad va de 1 a 3 y la recesión es la clasificación de Miller, de la clase 1 a la 4. */
export const MAX_GRADO: Readonly<Record<string, number>> = { movilidad: 3, recesion: 4 };
export type TipoHallazgo = (typeof TIPOS_HALLAZGO)[number];

export const ESTADOS = ["por_realizar", "realizado"] as const;
export type Estado = (typeof ESTADOS)[number];

export interface Hallazgo {
  diente: number | null;
  tipo: TipoHallazgo;
  estado: Estado;
  superficies: Superficie[];
  cie10: string | null;
  nota: string;
  /** Movilidad: grado 1 a 3. Recesión: clase de Miller 1 a 4. */
  grado?: number | null;
}

export const NOMBRE_TIPO: Readonly<Record<TipoHallazgo, string>> = {
  caries: "Caries", obturacion: "Obturación", ausente: "Pérdida por caries", extraccion: "Extracción", corona: "Corona",
  endodoncia: "Endodoncia", fractura: "Fractura", diagnostico: "Diagnóstico", sellante: "Sellante",
  incrustacion: "Incrustación", carilla: "Carilla", perno: "Perno muñón", implante: "Implante",
  puente: "Prótesis fija", removible: "Prótesis removible", erupcion: "En erupción", exfoliado: "Exfoliado",
  pulpotomia: "Pulpotomía", pulpectomia: "Pulpectomía", coronaacero: "Corona de acero",
  mantenedor: "Mantenedor de espacio", fluor: "Barniz de flúor",
  perdida_otra: "Pérdida (otra causa)", protesis_total: "Prótesis total", movilidad: "Movilidad", recesion: "Recesión",
  incluido: "Diente incluido o retenido", supernumerario: "Diente supernumerario", quiste: "Quiste o lesión por enucleación", apicectomia: "Apicectomía",
  traccion: "Tracción ortodóncica (ventana quirúrgica)", frenectomia: "Frenectomía o frenotomía", alveoloplastia: "Alveoloplastia o torus",
  ortodoncia: "Ortodoncia", armonizacion: "Armonización orofacial", estetica: "Estética dental", atm: "ATM y bruxismo",
};

/** Pieza FDI válida: permanentes 11–48, temporales 51–85. */
export function piezaValida(n: number): boolean {
  if (!Number.isInteger(n)) return false;
  const q = Math.floor(n / 10), p = n % 10;
  return (q >= 1 && q <= 4 && p >= 1 && p <= 8) || (q >= 5 && q <= 8 && p >= 1 && p <= 5);
}

export function esTemporal(n: number): boolean {
  return Math.floor(n / 10) >= 5;
}

const POS = ["", "incisivo central", "incisivo lateral", "canino", "primer premolar", "segundo premolar", "primer molar", "segundo molar", "tercer molar"];
const POS_T = ["", "incisivo central", "incisivo lateral", "canino", "primer molar", "segundo molar"];
const CUAD = ["", "superior derecho", "superior izquierdo", "inferior izquierdo", "inferior derecho"];

export function nombrePieza(n: number): string {
  const q = Math.floor(n / 10), p = n % 10;
  return q >= 5 ? `${POS_T[p]} temporal ${CUAD[q - 4]}` : `${POS[p]} ${CUAD[q]}`;
}

export type ResultadoValidacion<T> = { ok: true; valor: T } | { ok: false; errores: string[] };

/** Valida y normaliza un hallazgo recibido de la app, del dictado o de la IA. Nunca confía en la entrada. */
export function validarHallazgo(entrada: unknown): ResultadoValidacion<Hallazgo> {
  const e: string[] = [];
  const o = (entrada && typeof entrada === "object" ? entrada : {}) as Record<string, unknown>;
  const diente = o.diente === null || o.diente === undefined || o.diente === "" ? null : Number(o.diente);
  if (diente !== null && !piezaValida(diente)) e.push("La pieza debe ser FDI: 11–48 o 51–85.");
  const tipo = String(o.tipo ?? "") as TipoHallazgo;
  if (!TIPOS_HALLAZGO.includes(tipo)) e.push("Tipo de hallazgo desconocido.");
  const estado = String(o.estado ?? "por_realizar") as Estado;
  if (!ESTADOS.includes(estado)) e.push("Estado inválido.");
  const supIn = Array.isArray(o.superficies) ? o.superficies : typeof o.superficies === "string" ? o.superficies.split("") : [];
  const superficies = SUPERFICIES.filter((s) => supIn.map((x) => String(x).toUpperCase()).includes(s));
  const cie10 = o.cie10 ? String(o.cie10).toUpperCase().trim() : null;
  if (cie10 && !CIE10[cie10]) e.push(`Código CIE-10 no reconocido: ${cie10}.`);
  if (tipo === "diagnostico" && !cie10) e.push("Un diagnóstico necesita su código CIE-10.");
  if (["caries", "obturacion", "sellante", "incrustacion"].includes(tipo) && diente !== null && superficies.length === 0)
    e.push("Indica al menos una superficie.");
  if (diente === null && !["diagnostico", "fluor", "protesis_total", "frenectomia", "alveoloplastia", "quiste", "supernumerario"].includes(tipo)) e.push("Este hallazgo necesita una pieza.");
  let nota = typeof o.nota === "string" ? o.nota.slice(0, 200) : "";
  if (tipo === "protesis_total") {
    // la prótesis total es de toda una arcada: se registra sin pieza, con la arcada
    const arcada = /inferior|mandib|abajo/i.test(nota) ? "Inferior" : /superior|maxilar|arriba/i.test(nota) ? "Superior"
      : diente !== null ? ([1, 2, 5, 6].includes(Math.floor(diente / 10)) ? "Superior" : "Inferior") : null;
    if (!arcada) e.push("Indica la arcada de la prótesis total: superior o inferior.");
    nota = arcada ?? "";
  }
  let grado: number | null = null;
  if (TIPOS_CON_GRADO.includes(tipo)) {
    grado = Number(o.grado);
    if (!Number.isInteger(grado) || grado < 1 || grado > MAX_GRADO[tipo]) e.push(tipo === "movilidad" ? "La movilidad necesita un grado de 1 a 3." : "La recesión necesita una clase de Miller de 1 a 4.");
  }
  return e.length ? { ok: false, errores: e } : { ok: true, valor: { diente: tipo === "protesis_total" ? null : diente, tipo, estado, superficies, cie10, nota, grado } };
}

/**
 * Aplica un hallazgo nuevo sobre los existentes de la misma pieza:
 * reemplaza el mismo tipo y, si es una restauración existente, quita la caries de esas caras.
 */
export function combinarHallazgo<T extends Hallazgo>(existentes: T[], nuevo: Hallazgo): { eliminar: T[]; recortar: { de: T; superficies: Superficie[] }[] } {
  const eliminar: T[] = [];
  const recortar: { de: T; superficies: Superficie[] }[] = [];
  for (const h of existentes) {
    if (h.diente !== nuevo.diente) continue;
    const mismo = h.tipo === nuevo.tipo && (h.tipo !== "diagnostico" || h.cie10 === nuevo.cie10) &&
      (nuevo.superficies.length === 0 || h.superficies.join("") === nuevo.superficies.join(""));
    if (mismo) { eliminar.push(h); continue; }
    if (["obturacion", "incrustacion"].includes(nuevo.tipo) && nuevo.estado === "realizado" && h.tipo === "caries") {
      const quedan = h.superficies.filter((s) => !nuevo.superficies.includes(s));
      if (quedan.length === 0) eliminar.push(h);
      else if (quedan.length !== h.superficies.length) recortar.push({ de: h, superficies: quedan });
    }
  }
  return { eliminar, recortar };
}

/* ---------- plan de tratamiento ---------- */

export function codigoEndodoncia(n: number): string {
  const p = n % 10;
  return p <= 3 ? "END1" : p <= 5 ? "END2" : "END3";
}

/**
 * Material de una corona según la nota del hallazgo: metal-porcelana (COR), zirconio (CZR), cerámica (CCE) o disilicato de litio (CDL).
 * Sin material en la nota queda la corona de siempre (COR); `explicito` dice si la nota nombra el material.
 */
export function materialCorona(nota: string): { codigo: "COR" | "CZR" | "CCE" | "CDL"; abrev: string | null; nombre: string; explicito: boolean } {
  const t = nota.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (/zirconi/.test(t)) return { codigo: "CZR", abrev: "ZR", nombre: "Zirconio", explicito: true };
  if (/disilicato|e\.?max/.test(t)) return { codigo: "CDL", abrev: "DL", nombre: "Disilicato de litio", explicito: true };
  if (/ceramic/.test(t)) return { codigo: "CCE", abrev: "CE", nombre: "Cerámica", explicito: true };
  if (/metal/.test(t)) return { codigo: "COR", abrev: "MP", nombre: "Metal-porcelana", explicito: true };
  return { codigo: "COR", abrev: null, nombre: "Metal-porcelana", explicito: false };
}

/** Procedimiento del arancel que corresponde a un hallazgo por tratar (o null si no genera tratamiento). */
export function codigoTratamiento(h: Hallazgo): string | null {
  const n = h.diente, k = h.superficies.length, res = k >= 3 ? "RES3" : k === 2 ? "RES2" : "RES1";
  switch (h.tipo) {
    case "caries": return n !== null && esTemporal(n) ? "ION" : res;
    case "obturacion": return /amalgama/i.test(h.nota) ? "AMA" : /ion/i.test(h.nota) || (n !== null && esTemporal(n)) ? "ION" : res;
    case "fractura": return k ? res : "RES2";
    case "extraccion": return n !== null && !esTemporal(n) && n % 10 === 8 ? "EXQ" : "EXO";
    case "endodoncia": return n !== null ? (esTemporal(n) ? "PPC" : codigoEndodoncia(n)) : "END1";
    case "corona": return materialCorona(h.nota).codigo;
    case "coronaacero": return "CA";
    case "sellante": return "SEL";
    case "incrustacion": return "INC";
    case "carilla": return "CV";
    case "perno": return "PER";
    case "implante": return "IMP";
    case "puente": return "PF";
    case "removible": return "PR";
    case "protesis_total": return "PT";
    case "pulpotomia": return "PPT";
    case "pulpectomia": return "PPC";
    case "mantenedor": return "ME";
    case "fluor": return "FLU";
    // cirugía oral: el diente incluido y el supernumerario se cobran como exodoncia quirúrgica; el resto tiene su código
    case "incluido": case "supernumerario": return "EXQ";
    case "quiste": return "QUI";
    case "apicectomia": return "APX";
    case "traccion": return "VEN";
    case "frenectomia": return "FRE";
    case "alveoloplastia": return "ALV";
    default: return null;
  }
}

const URGENCIA = ["K04.0", "K04.1", "K04.4", "K04.5", "K04.6", "K04.7"];

/** Fase del plan (0 urgencia … 4 estética). Endodoncias y exodoncias con dolor o infección van primero. */
export function faseTratamiento(codigo: string, diente: number | null, hallazgos: Hallazgo[]): number {
  if (codigo === "CON" || codigo === "RX") return 0;
  if (codigo === "TIN" || codigo === "ABP") return 0;
  if (["PRO", "PRS", "IHO", "RAS", "FLU", "SEL"].includes(codigo)) return 1;
  if (codigo === "MPE") return 4;
  if (["END1", "END2", "END3", "PPT", "PPC", "EXO", "EXQ"].includes(codigo) && diente !== null &&
      hallazgos.some((h) => h.diente === diente && h.cie10 !== null && URGENCIA.includes(h.cie10))) return 0;
  if (["COR", "CZR", "CA", "PER", "IMP", "PF", "PR", "PT", "CV", "ME", "INC"].includes(codigo)) return 3;
  if (codigo === "BLA") return 4;
  return 2;
}

/* ---------- índices ---------- */

export const PERMANENTES = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28, 48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];
export const TEMPORALES = [55, 54, 53, 52, 51, 61, 62, 63, 64, 65, 85, 84, 83, 82, 81, 71, 72, 73, 74, 75];

export interface Indice { cariados: number; perdidos: number; obturados: number; total: number }

/**
 * CPO-D (permanentes) o ceo-d (temporales). Cada pieza cuenta una sola vez, con prioridad perdido > cariado > obturado.
 * En temporales solo la extracción indicada cuenta como "e"; una exfoliada no cuenta.
 */
export function indiceCariogenico(hallazgos: Hallazgo[], temporal: boolean): Indice {
  let c = 0, p = 0, o = 0;
  for (const n of temporal ? TEMPORALES : PERMANENTES) {
    const hs = hallazgos.filter((h) => h.diente === n);
    const perdido = temporal
      ? hs.some((h) => h.tipo === "extraccion" && h.estado === "por_realizar")
      : hs.some((h) => h.tipo === "ausente" || (h.tipo === "extraccion" && h.estado === "por_realizar"));
    const cariado = hs.some((h) => h.tipo === "caries");
    const obturado = hs.some((h) => ["obturacion", "incrustacion", "corona", "coronaacero"].includes(h.tipo) && h.estado === "realizado");
    if (perdido) p++; else if (cariado) c++; else if (obturado) o++;
  }
  return { cariados: c, perdidos: p, obturados: o, total: c + p + o };
}

/** Dentición a mostrar según la edad: temporal (< 6), mixta (6–12) o permanente. */
export function denticion(edadAnios: number | null): "temporal" | "mixta" | "permanente" {
  if (edadAnios === null) return "permanente";
  return edadAnios < 6 ? "temporal" : edadAnios <= 12 ? "mixta" : "permanente";
}

/* ---------- prótesis fija y removible: de pilar a pilar ---------- */

/** Filas del odontograma en el orden en que se dibujan (de derecha del paciente a izquierda). */
export const FILAS_FDI: readonly (readonly number[])[] = [
  [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28],
  [55, 54, 53, 52, 51, 61, 62, 63, 64, 65],
  [85, 84, 83, 82, 81, 71, 72, 73, 74, 75],
  [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38],
];
export const MIN_PIEZAS_PUENTE = 3;

/** Piezas de un puente, de un pilar al otro, en el orden de la fila (o null si no están en la misma fila). */
export function rangoPuente(a: number, b: number): number[] | null {
  const fila = FILAS_FDI.find((f) => f.includes(a) && f.includes(b));
  if (!fila) return null;
  const [i, j] = [fila.indexOf(a), fila.indexOf(b)].sort((x, y) => x - y);
  return fila.slice(i, j + 1);
}

/** Prótesis que se dibujan de pilar a pilar: la fija (recuadros y línea continua) y la removible (paréntesis y línea entrecortada). */
export const TIPOS_TRAMO = ["puente", "removible"] as const;
export type TipoTramo = (typeof TIPOS_TRAMO)[number];
const NOMBRE_TRAMO: Record<TipoTramo, string> = { puente: "Prótesis fija", removible: "Prótesis removible" };
export const esTramo = (t: string): t is TipoTramo => (TIPOS_TRAMO as readonly string[]).includes(t);

export const notaTramo = (tipo: TipoTramo, piezas: number[]) => {
  const [a, b] = [piezas[0], piezas[piezas.length - 1]].sort((x, y) => x - y);
  return `${NOMBRE_TRAMO[tipo]} ${a}–${b}`;
};
export const notaPuente = (piezas: number[]) => notaTramo("puente", piezas);

/**
 * Agrupa los hallazgos de prótesis fija (o removible) en tramos de pilar a pilar: por la nota «Prótesis fija 24–26»
 * o, en registros anteriores sin esa nota, por piezas seguidas de la misma fila.
 */
export function puentesDe<T extends Pick<Hallazgo, "diente" | "tipo" | "estado" | "nota">>(hs: T[], tipo: TipoTramo = "puente"): { tipo: TipoTramo; piezas: number[]; pilares: [number, number]; estado: Estado; hallazgos: T[] }[] {
  const pf = hs.filter((h) => h.tipo === tipo && h.diente !== null);
  const grupos = new Map<string, T[]>();
  const sueltos: T[] = [];
  for (const h of pf) {
    const m = h.nota.match(/(\d{2})\s*[–-]\s*(\d{2})/);
    const r = m ? rangoPuente(Number(m[1]), Number(m[2])) : null;
    if (r && r.includes(h.diente!)) { const k = `${r[0]}-${r[r.length - 1]}`; grupos.set(k, [...(grupos.get(k) ?? []), h]); }
    else sueltos.push(h);
  }
  for (const fila of FILAS_FDI) {
    let run: T[] = [];
    const cerrar = () => { if (run.length) grupos.set(`s${run[0].diente}`, run); run = []; };
    for (const n of fila) { const h = sueltos.find((x) => x.diente === n); if (h) run.push(h); else cerrar(); }
    cerrar();
  }
  return [...grupos.values()].map((g) => {
    const fila = FILAS_FDI.find((f) => f.includes(g[0].diente!))!;
    const piezas = g.map((h) => h.diente!).sort((x, y) => fila.indexOf(x) - fila.indexOf(y));
    return { tipo, piezas, pilares: [piezas[0], piezas[piezas.length - 1]] as [number, number], estado: g.some((h) => h.estado === "por_realizar") ? "por_realizar" : "realizado", hallazgos: g };
  });
}

/** Todos los tramos (fijos y removibles) de una lista de hallazgos. */
export const tramosDe = <T extends Pick<Hallazgo, "diente" | "tipo" | "estado" | "nota">>(hs: T[]) => TIPOS_TRAMO.flatMap((t) => puentesDe(hs, t));

/** Los hallazgos de prótesis fija o removible de un envío deben formar tramos completos: mínimo 3 piezas seguidas, de pilar a pilar. */
export function problemasPuentes(hs: Hallazgo[]): string[] {
  const p: string[] = [];
  for (const g of tramosDe(hs)) {
    const nombre = NOMBRE_TRAMO[g.tipo].toLowerCase();
    const r = rangoPuente(g.pilares[0], g.pilares[1]);
    const m = g.hallazgos[0].nota.match(/(\d{2})\s*[–-]\s*(\d{2})/);
    const declarado = m ? rangoPuente(Number(m[1]), Number(m[2])) : r;
    const tramo = (xs: number[]) => notaTramo(g.tipo, xs).replace(/^\S+ \S+ /, "");
    if (declarado && declarado.length !== g.piezas.length) p.push(`La ${nombre} ${tramo(declarado)} debe incluir todas las piezas de pilar a pilar.`);
    else if (g.piezas.length < MIN_PIEZAS_PUENTE) p.push(`La ${nombre} ${g.piezas.length === 1 ? g.piezas[0] : tramo(g.piezas)} necesita al menos ${MIN_PIEZAS_PUENTE} piezas: dos pilares y el póntico entre ellos.`);
  }
  return p;
}

/* ---------- del odontograma a las especialidades ---------- */

export interface PiezaSugerida { pieza: number; motivo: string }

/**
 * Piezas marcadas en el odontograma que piden una endodoncia o un implante, para ofrecerlas al abrir el
 * tratamiento. Primero las que el odontograma ya indica expresamente («endodoncia/implante por realizar»).
 */
export function piezasSugeridas(hs: readonly Pick<Hallazgo, "diente" | "tipo" | "estado" | "cie10">[], para: "endodoncia" | "implante"): PiezaSugerida[] {
  const peso = new Map<number, { motivo: string; orden: number }>();
  const poner = (n: number, motivo: string, orden: number) => { const x = peso.get(n); if (!x || orden < x.orden) peso.set(n, { motivo, orden }); };
  const conTratamiento = new Set(hs.filter((h) => h.diente !== null && h.estado === "realizado" && (para === "implante" ? h.tipo === "implante" : ["endodoncia", "pulpectomia"].includes(h.tipo))).map((h) => h.diente!));
  for (const h of hs) {
    const n = h.diente;
    if (n === null || !piezaValida(n)) continue;
    if (para === "implante") {
      if (n > 48) continue;
      if (h.tipo === "implante" && h.estado === "por_realizar") poner(n, "Implante por realizar", 0);
      else if (h.tipo === "ausente" || h.tipo === "perdida_otra") poner(n, NOMBRE_TIPO[h.tipo], 1);
      else if (h.tipo === "extraccion") poner(n, h.estado === "realizado" ? "Extraída" : "Extracción indicada", 2);
      else if (h.cie10 === "K08.1" || h.cie10 === "K08.3") poner(n, h.cie10 === "K08.1" ? "Pérdida de la pieza" : "Raíz retenida", 3);
    } else {
      if ((h.tipo === "endodoncia" || h.tipo === "pulpectomia") && h.estado === "por_realizar") poner(n, `${NOMBRE_TIPO[h.tipo]} por realizar`, 0);
      else if (h.cie10 && /^K04\.[0-7]$/.test(h.cie10)) poner(n, CIE_PULPAR[h.cie10] ?? "Diagnóstico pulpar", 1);
      else if (h.tipo === "pulpotomia" && h.estado === "por_realizar") poner(n, "Pulpotomía por realizar", 2);
    }
  }
  return [...peso.entries()]
    .filter(([n, x]) => !(conTratamiento.has(n) && x.orden > 0))
    .sort((a, b) => a[1].orden - b[1].orden || a[0] - b[0])
    .map(([pieza, x]) => ({ pieza, motivo: x.motivo }));
}
const CIE_PULPAR: Record<string, string> = {
  "K04.0": "Pulpitis", "K04.1": "Necrosis pulpar", "K04.2": "Degeneración pulpar", "K04.3": "Tejido duro en la pulpa",
  "K04.4": "Periodontitis apical aguda", "K04.5": "Periodontitis apical crónica", "K04.6": "Absceso con fístula", "K04.7": "Absceso sin fístula",
};
